import { useEffect, useState, useRef } from "react";
import { Plus, Trash2, Upload } from "lucide-react";
import api, { apiError, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";

const POLICY_KEYS = [
    { key: "about", label: "About ELVOSH" },
    { key: "warranty", label: "Warranty" },
    { key: "shipping", label: "Shipping Policy" },
    { key: "return", label: "Return & Refund" },
    { key: "privacy", label: "Privacy Policy" },
    { key: "terms", label: "Terms & Conditions" },
];

const inp = "w-full border border-gray-300 rounded px-3 py-2 text-sm outline-none focus:border-gold";

export default function AdminContent() {
    return (
        <div className="max-w-3xl">
            <h1 className="text-2xl font-semibold mb-6">Content & CMS</h1>
            <Tabs defaultValue="homepage">
                <TabsList className="mb-6"><TabsTrigger value="homepage">Homepage</TabsTrigger><TabsTrigger value="faq">FAQ</TabsTrigger><TabsTrigger value="policies">Policies</TabsTrigger></TabsList>
                <TabsContent value="homepage"><Homepage /></TabsContent>
                <TabsContent value="faq"><FaqManager /></TabsContent>
                <TabsContent value="policies"><Policies /></TabsContent>
            </Tabs>
        </div>
    );
}

function Homepage() {
    const [s, setS] = useState(null);
    const fileRef = useRef();
    useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data.settings.homepage || {})); }, []);
    if (!s) return <p className="text-gray-400">Loading…</p>;
    const set = (k, v) => setS({ ...s, [k]: v });
    const save = async () => { try { await api.put("/admin/settings", { homepage: s }); toast.success("Homepage saved"); } catch (e) { toast.error(apiError(e)); } };
    const uploadHero = async (file) => { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); set("hero_image", data.url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${data.url}` : data.url); toast.success("Hero uploaded"); };

    const toggles = [["show_featured", "Featured"], ["show_best_sellers", "Best Sellers"], ["show_new_arrivals", "New Arrivals"], ["show_categories", "Categories"], ["show_reviews", "Reviews"], ["show_video_reviews", "Video Reviews"], ["show_before_after", "Before & After"], ["show_instagram", "Instagram"]];
    return (
        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
            <div><label className="text-xs uppercase text-gray-500">Hero Title</label><input value={s.hero_title || ""} onChange={(e) => set("hero_title", e.target.value)} className={inp} /></div>
            <div><label className="text-xs uppercase text-gray-500">Hero Subtitle</label><textarea rows={2} value={s.hero_subtitle || ""} onChange={(e) => set("hero_subtitle", e.target.value)} className={inp} /></div>
            <div><label className="text-xs uppercase text-gray-500">Promo Banner Text</label><input value={s.promo_text || ""} onChange={(e) => set("promo_text", e.target.value)} className={inp} /></div>
            <div className="flex items-center gap-3">
                {s.hero_image && <img src={s.hero_image} alt="" className="h-16 w-24 object-cover rounded" />}
                <button onClick={() => fileRef.current.click()} className="border border-gray-300 rounded px-3 py-2 text-sm flex items-center gap-2"><Upload className="h-4 w-4" /> Hero Image</button>
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && uploadHero(e.target.files[0])} />
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2">
                {toggles.map(([k, l]) => (
                    <div key={k} className="flex items-center justify-between border border-gray-200 rounded px-3 py-2"><span className="text-sm">{l}</span><Switch checked={s[k] !== false} onCheckedChange={(v) => set(k, v)} /></div>
                ))}
            </div>
            <button data-testid="save-homepage" onClick={save} className="bg-ink text-white px-6 py-2.5 rounded text-sm">Save Homepage</button>
        </div>
    );
}

function FaqManager() {
    const [faqs, setFaqs] = useState([]);
    const [draft, setDraft] = useState({ question: "", answer: "", category: "General" });
    const load = () => api.get("/admin/faq").then(({ data }) => setFaqs(data.faqs));
    useEffect(() => { load(); }, []);
    const add = async () => { if (!draft.question) return; await api.post("/admin/faq", { ...draft, order: faqs.length }); setDraft({ question: "", answer: "", category: "General" }); load(); toast.success("FAQ added"); };
    const remove = async (id) => { await api.delete(`/admin/faq/${id}`); load(); };
    return (
        <div className="space-y-4">
            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-3">
                <input placeholder="Question" value={draft.question} onChange={(e) => setDraft({ ...draft, question: e.target.value })} className={inp} data-testid="faq-question" />
                <textarea placeholder="Answer" rows={3} value={draft.answer} onChange={(e) => setDraft({ ...draft, answer: e.target.value })} className={inp} data-testid="faq-answer" />
                <input placeholder="Category" value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} className={inp} />
                <button data-testid="faq-add" onClick={add} className="bg-ink text-white px-5 py-2 rounded text-sm flex items-center gap-2"><Plus className="h-4 w-4" /> Add FAQ</button>
            </div>
            {faqs.map((f) => (
                <div key={f.id} className="bg-white border border-gray-200 rounded-lg p-4 flex justify-between items-start">
                    <div><p className="font-medium text-sm">{f.question}</p><p className="text-xs text-gray-500 mt-1">{f.answer}</p><span className="text-[10px] uppercase text-gold">{f.category}</span></div>
                    <button onClick={() => remove(f.id)} className="text-red-500 p-1"><Trash2 className="h-4 w-4" /></button>
                </div>
            ))}
        </div>
    );
}

function Policies() {
    const [active, setActive] = useState("about");
    const [data, setData] = useState({ title: "", content: "" });
    useEffect(() => { api.get(`/policies/${active}`).then(({ data }) => setData(data.policy)).catch(() => setData({ title: POLICY_KEYS.find((p) => p.key === active).label, content: "" })); }, [active]);
    const save = async () => { try { await api.put(`/admin/policies/${active}`, data); toast.success("Policy saved"); } catch (e) { toast.error(apiError(e)); } };
    return (
        <div>
            <div className="flex flex-wrap gap-2 mb-4">
                {POLICY_KEYS.map((p) => <button key={p.key} onClick={() => setActive(p.key)} className={`px-3 py-1.5 text-sm rounded ${active === p.key ? "bg-ink text-white" : "bg-white border border-gray-200"}`}>{p.label}</button>)}
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-3">
                <input value={data.title || ""} onChange={(e) => setData({ ...data, title: e.target.value })} className={inp} placeholder="Title" />
                <textarea rows={12} value={data.content || ""} onChange={(e) => setData({ ...data, content: e.target.value })} className={inp} data-testid="policy-content" />
                <button data-testid="policy-save" onClick={save} className="bg-ink text-white px-6 py-2.5 rounded text-sm">Save Policy</button>
            </div>
        </div>
    );
}
